import { z } from "zod";
import { EServiceType, EServiceCategory } from "./common";
import { userDataSchema } from "./user";
import { petSchema, petNotesResponseSchema } from "./pet";
import { addressSchema } from "./location";
import { geoLocationSchema } from "./geo";
import { partnerSchema } from "./partner";
import { paginationMetaSchema } from "./base-api-response";
import { createPaginatedResponseSchema, PaginationDto } from "./pagination";
import { productSingleVariantResponseSchema } from "./schemas/product-v4.schemas";
import {
  ESessionStatus,
  AdminRescheduleReasonEnum,
  AdminCancellationReasonEnum,
  AssignmentSource,
  SessionChangeAction,
  SessionTiming,
  PartnerImplicationStatus,
  PartnerLeaveImplication,
  RequestSource,
  EUserTypeV4,
  SessionRefundImplication,
  SessionRefundBasis,
} from "../constants";

/**
 * Session schema with Zod validation matching backend sessionResponseSchema
 * Comprehensive schema for detailed session data from get session by ID API
 */
export const sessionSchema = z.object({
  id: z.string(),
  user: userDataSchema,
  serviceCategory: z.nativeEnum(EServiceCategory),
  serviceType: z.nativeEnum(EServiceType),
  partner: partnerSchema.nullish(),
  status: z.nativeEnum(ESessionStatus),
  scheduledAt: z.number(),
  rescheduledAt: z.number().nullish(),
  pets: z.array(petSchema),
  petNotes: z.record(z.string(), petNotesResponseSchema).nullish(),
  startedAt: z.number().optional(),
  endedAt: z.number().optional(),
  duration: z.number(),
  address: addressSchema,
  geoLocation: geoLocationSchema,
  cancellationReason: z.string().optional(),
  cancelledAt: z.number().optional(),
  cancelledBy: z.string().optional(),
  cancellationProofUrl: z.string().optional(),
  rescheduledSessionId: z.string().optional(),
  originalSessionId: z.string().optional(),
  rescheduledBy: z.string().optional(),
  rescheduleReason: z.string().optional(),
  hasUniformViolation: z.boolean().optional(),
  isCheckedIn: z.boolean().optional(),
  checkedInAt: z.number().optional(),
  isAtLocation: z.boolean().optional(),
  products: z.array(
    productSingleVariantResponseSchema.extend({
      quantity: z.number().nullish(),
    }),
  ),
  productVariantItems: z.array(
    z.object({
      productId: z.string(),
      variantId: z.string(),
      quantity: z.number(),
    })
  ),
  reviewRating: z.number().optional(),
  reviewComments: z.string().optional(),
  reviewCreatedAt: z.number().optional(),
  reviewUpdatedAt: z.number().optional(),

  // Check-in images
  selfieImageUrl: z.string().optional(),
  tshirtImageUrl: z.string().optional(),
  shoesImageUrl: z.string().optional(),
  kitImageUrl: z.string().optional(),
  beforeImages: z.array(z.string()).optional(),
  afterImages: z.array(z.string()).optional(),
  afterSessionCleanupImageUrl: z.string().optional(),

  // Document verification fields
  selfieVerificationStatus: z.string().optional(), // Would be z.nativeEnum(VerificationStatus) with proper enum
  selfieVerifiedAt: z.number().optional(),
  selfieVerifiedBy: z.string().optional(),
  tshirtVerificationStatus: z.string().optional(),
  tshirtVerifiedAt: z.number().optional(),
  tshirtVerifiedBy: z.string().optional(),
  shoesVerificationStatus: z.string().optional(),
  shoesVerifiedAt: z.number().optional(),
  shoesVerifiedBy: z.string().optional(),
  kitVerificationStatus: z.string().optional(),
  kitVerifiedAt: z.number().optional(),
  kitVerifiedBy: z.string().optional(),
  beforeImagesVerificationStatus: z.string().optional(),
  beforeImagesVerifiedAt: z.number().optional(),
  beforeImagesVerifiedBy: z.string().optional(),
  afterImagesVerificationStatus: z.string().optional(),
  afterImagesVerifiedAt: z.number().optional(),
  afterImagesVerifiedBy: z.string().optional(),
  afterSessionCleanupImageVerificationStatus: z.string().optional(),
  afterSessionCleanupImageVerifiedAt: z.number().optional(),
  afterSessionCleanupImageVerifiedBy: z.string().optional(),

  isOtpVerificationRequired: z.boolean(),
  isOtpVerified: z.boolean(),
  otp: z.string().optional(),

  // Payment fields
  paymentAmount: z.string().optional(),
  paymentMethod: z.string().optional(), // Would be z.nativeEnum(PaymentMethodV4) with proper enum
  mongoPaymentId: z.string().optional(),
  paymentStatus: z.string().optional(), // Would be z.nativeEnum(PaymentStatusV4) with proper enum
  paymentCollectedAt: z.number().optional(),
  paymentCollectedBy: z.string().optional(),
  paymentCollectionMethod: z.string().optional(),
  paymentCollectionReceiptNumber: z.string().optional(),
  paymentCollectionNotes: z.string().optional(),
  isPaymentVerified: z.boolean().optional(),
  paymentVerifiedAt: z.number().optional(),
  paymentVerifiedBy: z.string().optional(),

  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string(),
  updatedBy: z.string().optional(),

  // Route map tracking
  routeMapUrl: z.string().optional(), // S3 URL for permanent map image
  routeMapGeneratedAt: z.number().optional(), // When permanent map was generated

  // Distance tracking
  distanceTravelledMeters: z.number().optional(), // Total distance travelled in meters

  // Subscription information
  subscriptionId: z.string().optional(), // ID of associated subscription for subscription-based sessions

  // Order information
  orderId: z.string().optional(), // ID of associated order for sessions booked via orders

  // Assignment tracking
  assignedAt: z.number().optional(), // When partner was assigned (epoch milliseconds)
  assignedBy: z.string().optional(), // User ID who assigned (user/partner/admin)
  assignmentSource: z.nativeEnum(AssignmentSource).optional(), // How it was assigned (AUTO_MATCHING or MANUAL)
});

export type ISession = z.infer<typeof sessionSchema>;

/**
 * Session list response schema matching actual API
 */
export const sessionListResponseSchema = z.object({
  items: z.array(sessionSchema),
  meta: paginationMetaSchema,
});

export type ISessionsResponse = z.infer<typeof sessionListResponseSchema>;

/**
 * Zod schema for searching sessions (matching backend DTO)
 * Allows filtering by various criteria including scheduledAt range, service category,
 * status, service type, location, partner, and customer
 */
export const searchSessionsRequestSchema = z.object({
  // Pagination
  page: z.coerce.number().int().min(1).default(1).nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(10).nullish(),

  // ScheduledAt range filters
  scheduledAtFrom: z.number().int().positive().nullish(),
  scheduledAtTo: z.number().int().positive().nullish(),

  // Service filters
  serviceCategory: z.nativeEnum(EServiceCategory).nullish(),
  serviceType: z.nativeEnum(EServiceType).nullish(),

  // Status filter
  status: z.nativeEnum(ESessionStatus).nullish(),

  // Location filters
  cityCode: z.string().nullish(),
  subCityCode: z.string().nullish(),
  hubCode: z.string().nullish(),
  countryCode: z.string().nullish(),

  // Partner filters
  partnerName: z.string().nullish(),
  partnerPhoneNumber: z.string().nullish(),

  // Customer filter
  customerPhoneNumber: z.string().nullish(),

  // Session assignment request filters
  sessionAssignmentRequestId: z.string().uuid().nullish(),
  sessionAssignmentRequestCycleId: z.string().uuid().nullish(),
  sessionAssignmentRequestAttemptId: z.string().uuid().nullish(),

  // Subscription filter
  subscriptionId: z.string().nullish(),

  // Search filter
  search: z.string().optional(),

  // Filter by (compound filter)
  filterBy: z.string().nullish(),
});

export type SearchSessionsRequest = z.infer<typeof searchSessionsRequestSchema>;

export interface ISessionFilterOption {
  value: string;
  label: string;
}

export interface ISessionFilterValues {
  status?: ESessionStatus;
  serviceType?: EServiceType;
  serviceCategory?: EServiceCategory;
  dateRange?: {
    from?: number;
    to?: number;
  };
  location?: {
    cityCode?: string;
    subCityCode?: string;
    hubCode?: string;
    countryCode?: string;
  };
  partner?: {
    name?: string;
    phoneNumber?: string;
  };
  customer?: {
    phoneNumber?: string;
  };
  sessionAssignmentRequestId?: string;
  sessionAssignmentRequestCycleId?: string;
  sessionAssignmentRequestAttemptId?: string;
  subscriptionId?: string;
}

export const basicSessionResponseSchema = z.object({
  id: z.string(),
  scheduledAt: z.number(),
  duration: z.number(),
  status: z.nativeEnum(ESessionStatus),
  serviceCategory: z.nativeEnum(EServiceCategory),
  serviceType: z.nativeEnum(EServiceType),
});

export type BasicSessionResponseDto = z.infer<
  typeof basicSessionResponseSchema
>;

/**
 * Sessions summary response schema (corresponds to SessionsSummaryDo)
 */
export const sessionsSummaryResponseSchema = z.object({
  totalSessions: z.number(),
  completedSessions: z.number(),
  upcomingSessions: z.number(),
});

/**
 * Type for sessions summary response
 */
export type SessionsSummaryResponseDto = z.infer<
  typeof sessionsSummaryResponseSchema
>;

/**
 * Reschedule session request schema
 */
export const rescheduleSessionRequestSchema = z.object({
  scheduledAt: z.number().positive("Scheduled time must be a positive number"),
  reason: z.nativeEnum(AdminRescheduleReasonEnum),
  rescheduleReason: z.string().optional(),
});

export type IRescheduleSessionRequest = z.infer<
  typeof rescheduleSessionRequestSchema
>;

/**
 * Cancel session request schema
 */
export const cancelSessionRequestSchema = z.object({
  reason: z.nativeEnum(AdminCancellationReasonEnum),
  comment: z.string().optional(),
});

export type ICancelSessionRequest = z.infer<typeof cancelSessionRequestSchema>;

/**
 * Mark session as complete request schema
 */
export const completeSessionRequestSchema = z.object({
  comment: z.string().optional(),
});

export type ICompleteSessionRequest = z.infer<
  typeof completeSessionRequestSchema
>;

/**
 * Schema for assign partner to session request
 */
export const assignPartnerRequestSchema = z.object({
  partnerId: z.string().uuid("Invalid partner ID"),
});

export type AssignPartnerRequestDto = z.infer<
  typeof assignPartnerRequestSchema
>;

/**
 * Schema for complete action data (for COMPLETE action)
 */
export const completeActionDataSchema = z.object({
  startedAt: z.number().int().positive(),
  endedAt: z.number().int().positive(),
  selfieImageUrl: z.string().url().optional(),
  tshirtImageUrl: z.string().url().optional(),
  shoesImageUrl: z.string().url().optional(),
  kitImageUrl: z.string().url().optional(),
  beforeImages: z.array(z.string().url()).optional(),
  afterImages: z.array(z.string().url()).optional(),
  afterSessionCleanupImageUrl: z.string().url().optional(),
  markAsPaid: z.boolean().optional(),
});

/**
 * Schema for action data
 */
export const actionDataSchema = z.union([completeActionDataSchema, z.record(z.unknown())])
  .optional()

export type IActionData = z.infer<typeof actionDataSchema>;

/**
 * Schema for creating a session change request (aligns with backend CreateSessionChangeRequestDto)
 */
export const createSessionChangeRequestSchema = z.object({
  reason: z.string(),
  action: z.nativeEnum(SessionChangeAction).nullish(),
  comment: z.string().trim().min(1).optional(),
  proofUrl: z.string().url().optional(),
  newScheduledAt: z.number().int().positive().optional(),
  actionData: actionDataSchema,
  source: z.nativeEnum(RequestSource),
  /**
   * Opt in to refunding the customer. Only meaningful when the resolved implication is
   * ELIGIBLE — AUTOMATIC refunds regardless, and sending `true` against NOT_APPLICABLE is
   * rejected with a 400, so only send it when the options endpoint says ELIGIBLE.
   */
  requestRefund: z.boolean().optional(),
});

export type ICreateSessionChangeRequest = z.infer<
  typeof createSessionChangeRequestSchema
>;

/**
 * Partner implication in session change request response (cancellation adjustment, penalty, leave)
 */
export const sessionChangeRequestPartnerImplicationSchema = z.object({
  cancellationAdjustmentImplication: z.nativeEnum(PartnerImplicationStatus),
  penaltyImplication: z.nativeEnum(PartnerImplicationStatus),
  leaveImplication: z.nativeEnum(PartnerLeaveImplication),
});

/**
 * Customer refund implication in session change request response
 */
export const sessionChangeRequestCustomerImplicationSchema = z.object({
  refundImplication: z.nativeEnum(SessionRefundImplication),
  refundBasis: z.nativeEnum(SessionRefundBasis),
});

/**
 * Schema for session change request response (aligns with backend SessionChangeRequestResponseDto)
 */
export const sessionChangeRequestResponseSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  source: z.string(),
  reason: z.string(),
  timing: z.nativeEnum(SessionTiming),
  requestedBy: z.string(),
  requestedByUserType: z.string(),
  comment: z.string().optional(),
  proofUrl: z.string().url().optional(),
  action: z.nativeEnum(SessionChangeAction),
  partnerImplication: sessionChangeRequestPartnerImplicationSchema,
  status: z.string(),
  session: sessionSchema,
  wasRescheduled: z.boolean(),
  partnerUnassigned: z.boolean(),
  assignmentJobPublished: z.boolean(),
  basePayCreated: z.boolean(),
  penaltyCreated: z.boolean(),
  approvalTasksCreated: z.boolean(),
  basePayAutoApproved: z.boolean().optional(),
  penaltyAutoApproved: z.boolean().optional(),
  leaveCreated: z.boolean().optional(),
  /** Refund policy the rule engine resolved for this request. */
  customerImplication: sessionChangeRequestCustomerImplicationSchema.optional(),
  /** Whether a refund was requested (opted in, or forced by an AUTOMATIC implication). */
  refundRequested: z.boolean().optional(),
  /** Whether refund request record(s) were actually written. */
  refundRequestCreated: z.boolean().optional(),
  /** IDs of the created refund requests, each awaiting admin approval. */
  refundRequestIds: z.array(z.string().uuid()).optional(),
  /** Total across the created refund requests, in paise. */
  refundAmountPaise: z.number().optional(),
  /** Why no refund was created despite an eligible implication (e.g. postpaid session). */
  refundBlockedReason: z.string().optional(),
  createdAt: z.number().int().positive(),
});

export type ISessionChangeRequestResponse = z.infer<
  typeof sessionChangeRequestResponseSchema
>;

/**
 * Schema for what a session change request would do, without doing it
 * (aligns with backend SessionChangeOptionsResponseDto).
 */
export const sessionChangeOptionsResponseSchema = z.object({
  sessionId: z.string().uuid(),
  timing: z.nativeEnum(SessionTiming),
  hasPartnerAssigned: z.boolean(),
  allowedActions: z.array(z.nativeEnum(SessionChangeAction)),
  refund: z.object({
    implication: z.nativeEnum(SessionRefundImplication),
    basis: z.nativeEnum(SessionRefundBasis),
    /** What would actually be refunded, in paise. Zero when nothing is refundable. */
    estimatedRefundAmountPaise: z.number(),
    /**
     * Set when policy allows a refund but it cannot currently be issued. The control is
     * shown disabled with this text.
     */
    blockedReason: z.string().optional(),
  }),
  /**
   * Cash the partner would collect from the customer if the session is marked paid on COMPLETE
   * (the "mark as paid" toggle). For BOOKING_FEE orders this is the remaining balance, not the
   * full total, since the booking fee was already collected via the gateway.
   */
  cashCollection: z.object({
    /** True when marking paid would record a cash collection and debit the partner. */
    applicable: z.boolean(),
    /** Order payment mode driving the amount (e.g. "POSTPAID", "BOOKING_FEE"). */
    paymentMode: z.string().optional(),
    /** Amount the partner would collect in cash and be debited, in paise. */
    pendingAmountPaise: z.number(),
  }),
});

export type ISessionChangeOptionsResponse = z.infer<
  typeof sessionChangeOptionsResponseSchema
>;

export const sessionChangeRequestHistoryItemSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  source: z.nativeEnum(RequestSource),
  reason: z.string(),
  timing: z.nativeEnum(SessionTiming),
  requestedBy: z.string(),
  requestedByUserType: z.nativeEnum(EUserTypeV4),
  comment: z.string().nullish(),
  proofUrl: z.string().url().nullish(),
  action: z.nativeEnum(SessionChangeAction),
  partnerImplication: sessionChangeRequestPartnerImplicationSchema,
  status: z.string(),
  actionData: z.unknown().nullish(),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
});

export const sessionChangeRequestHistoryResponseSchema = createPaginatedResponseSchema(sessionChangeRequestHistoryItemSchema);

export type ISessionChangeRequestHistoryItem = z.infer<typeof sessionChangeRequestHistoryItemSchema>;
export type ISessionChangeRequestHistoryResponse = z.infer<typeof sessionChangeRequestHistoryResponseSchema>;

export type SessionChangeRequestHistoryQuery = PaginationDto;

